<template>
  <div>
    <v-btn ref="opener" class="personal-sheet-tab" color="primary" :aria-expanded="String(open)" aria-controls="personal-sheet-panel" @click="open = true">
      パーソナルシート
    </v-btn>
    <v-dialog v-model="open" :fullscreen="$vuetify.breakpoint.smAndDown" max-width="820" content-class="personal-sheet-dialog" scrollable>
      <v-card ref="panel" id="personal-sheet-panel" class="personal-sheet-panel" role="region" aria-labelledby="personal-sheet-title">
        <v-card-title class="d-flex flex-nowrap">
          <span id="personal-sheet-title" class="text-h6">パーソナルシート</span>
          <v-spacer />
          <v-btn icon aria-label="パーソナルシートを閉じる" @click="open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="sheet-body">
          <p>個人情報カードを見て入力してください。黄色の欄は自分で入力する学習用メモです。</p>
          <p>商品選択の金額とは別に計算します。NMIは年間総収入と月額の税金・年金保険料・健康／社会保険料から自動計算します。</p>
          <p>{{ retentionMessage }}</p>
          <v-alert v-if="hasErrors" type="error" dense text role="alert">入力内容を確認してください。赤枠の項目を修正して、もう一度確認してください。</v-alert>
          <v-row dense>
            <v-col v-for="field in fields" :key="field.key" cols="12" sm="6">
              <v-select v-if="field.choices && !field.automatic" :data-sheet-key="'fields-' + field.key" :error-messages="validationErrors.fields[field.key] || []" :label="field.label" :items="field.choices" :value="personalSheetSpouseValue(sheet)" :suffix="personalSheetSpouseValue(sheet) && personalSheetSpouseValue(sheet) !== 'none' ? '歳' : undefined" clearable outlined dense background-color="#fff9c4" @input="update('fields', field.key, $event)" />
              <v-text-field v-else :data-sheet-key="'fields-' + field.key" :label="field.label" :value="field.yen && (field.automatic || focusedField !== field.key) ? formatSheetMoney(sheet.fields[field.key], field.signed) : displayField(field)" :readonly="!!field.automatic" :aria-describedby="field.key === 'nmi' ? 'nmi-calculation-note' : undefined" :hint="field.key === 'nmi' ? undefined : field.key === 'lucky_card' ? autoValues.luckyHint : field.automatic ? 'ログイン情報から自動入力しています。' : undefined" :persistent-hint="!!field.automatic || field.key.startsWith('child')" :error-messages="validationErrors.fields[field.key] || (field.key === 'nmi' ? autoValues.nmiError : [])" :suffix="field.yen ? '円' : (field.key === 'spouse_age' && sheet.fields.is_married === 'いない' || field.key.startsWith('child') && isNoChild(sheet.fields[field.key]) ? undefined : field.key === 'spouse_age' ? '歳' : field.unit)" :inputmode="field.numeric && !field.signed && !field.key.startsWith('child') ? 'numeric' : 'text'" :rules="field.numeric ? [value => (field.key.startsWith('child') && isNoChild(value)) || valid(value, field.signed)] : []" maxlength="40" outlined dense :background-color="field.automatic ? '#eceff1' : '#fff9c4'" @focus="focusedField = field.key" @blur="focusedField = null" @input="update('fields', field.key, $event)" />
            </v-col>
          </v-row>
          <p id="nmi-calculation-note" class="sheet-calculation-note">年間総収入 ÷ 12 − 税金 − 年金保険料 − 健康・社会保険料（円未満四捨五入）。4項目を入力してください。</p>
          <p class="mb-2">上下限はNMIを基準に計算します。ラッキーカードは含めません。</p>
          <div class="sheet-table-wrap" tabindex="0" aria-label="各費目の金額表。横にスクロールできます">
            <table class="sheet-table">
              <caption class="text-left pb-2">各費目の予算と決定額</caption>
              <thead><tr><th scope="col">費目</th><th scope="col">下限</th><th scope="col">自分で決めた金額</th><th scope="col">上限</th></tr></thead>
              <tbody>
                <tr v-for="row in rows" :key="row.key">
                  <th scope="row">{{ row.label }}</th>
                  <td class="sheet-lower"><span class="sheet-rate">{{ row.lowerRate }}％</span>{{ yen(row.lowerAmount) }}</td>
                  <td class="sheet-input-cell">
                    <v-text-field :data-sheet-key="'amounts-' + row.key" :error-messages="validationErrors.amounts[row.key] || []" :aria-label="row.label + 'の決定額'" :value="sheet.amounts[row.key]" inputmode="numeric" suffix="円" :rules="[value => valid(value)]" maxlength="16" outlined dense background-color="#fff9c4" @input="update('amounts', row.key, $event)" />
                  </td>
                  <td v-if="row.upperRate === null" class="sheet-upper">上限なし</td>
                  <td v-else class="sheet-upper"><span class="sheet-rate">{{ row.upperRate }}％</span>{{ yen(row.upperAmount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="mt-3 mb-0">{{ retentionMessage }}</p>
        </v-card-text>
        <v-divider />
        <div class="sheet-totals pa-4" aria-live="polite" aria-atomic="true">
          <v-btn v-if="!sheet.totals_confirmed" color="primary" class="mb-2" @click="confirmTotals">合計・残金を確認</v-btn>
          <template v-if="showTotals">
            <div class="d-flex justify-space-between"><span>支出合計</span><strong>{{ yen(totals.total) }}</strong></div>
            <div class="d-flex justify-space-between mt-2"><span>残金</span><strong :class="{'error--text': totals.balance < 0}">{{ yen(totals.balance) }}</strong></div>
            <div class="text-caption mt-2">残金 ＝ NMI ＋ ラッキーカード − 支出合計</div>
          </template>
          <p v-else class="text-caption mb-0">{{ sheet.totals_confirmed ? '入力不足・入力エラーのため計算できません。修正すると結果を表示します。' : '計算に必要な情報と全17費目を入力し、「合計・残金を確認」を押してください。' }}</p>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { validatePersonalSheet, hasPersonalSheetErrors, isNoChild } from '../utils/personal-sheet-validation'
import { PERSONAL_FIELDS, formatSheetMoney, personalSheetSpouseValue, resolvedPersonalSheet, personalSheetAutoValues, parseSheetAmount, personalSheetRows, personalSheetTotals, personalSheetCalculationErrors, canDisplayPersonalSheetTotals } from '../utils/personal-sheet'

export default {
  data: () => ({ open: false, validationActive: false, calculationActive: false, retentionMessage: '閉じても入力内容は残ります。同じタブの再読み込みでも保持されます。新しくログインするとリセットされます。', focusedField: null, fields: PERSONAL_FIELDS.filter(field => !field.hidden) }),
  computed: {
    validationErrors() {
      const submit = this.validationActive ? validatePersonalSheet(this.$store.state) : { fields: {}, amounts: {} }
      const calculation = this.calculationActive || this.sheet.totals_confirmed ? personalSheetCalculationErrors(this.sheet) : { fields: {}, amounts: {} }
      return { fields: { ...calculation.fields, ...submit.fields }, amounts: { ...calculation.amounts, ...submit.amounts } }
    },
    showTotals() { return canDisplayPersonalSheetTotals(this.sheet) },
    hasErrors() { return hasPersonalSheetErrors(this.validationErrors) },
    autoValues() { return personalSheetAutoValues(this.$store.state.personal_sheet || { fields: {} }, this.$store.state.login_response) },
    sheet() { return resolvedPersonalSheet(this.$store.state) },
    rows() { return personalSheetRows(this.sheet, this.$store.state.login_response, this.$store.state.target) },
    totals() { return personalSheetTotals(this.sheet) }
  },
  methods: {
    isNoChild,
    displayField(field) {
      return field.key === 'spouse_age' && this.sheet.fields.is_married === 'いない' ? 'いない' : this.sheet.fields[field.key]
    },
    async confirmTotals() {
      this.calculationActive = true
      const errors = personalSheetCalculationErrors(this.sheet)
      if (hasPersonalSheetErrors(errors)) { await this.focusFirstError(errors); return }
      this.$store.commit('confirm_personal_sheet_totals')
    },
    async showValidationErrors() {
      this.validationActive = true
      await this.focusFirstError(this.validationErrors)
    },
    async focusFirstError(errors) {
      this.open = true
      await this.$nextTick()
      // Wait for Vuetify's dialog transition before moving focus.
      setTimeout(() => {
        if (!this.open) return
        const section = Object.keys(errors.fields).length ? 'fields' : 'amounts'
        const ordered = section === 'fields' ? this.fields : this.rows
        const first = ordered.find(({ key }) => errors[section][key])
        if (!first) return
        const key = first.key
        const control = this.$refs.panel.$el.querySelector(`input[data-sheet-key="${section}-${key}"]`)
        if (control) { control.focus(); control.scrollIntoView({ block: 'center' }) }
      }, 300)
    },
    personalSheetSpouseValue,
    formatSheetMoney,
    update(section, key, value) {
      if (section === 'fields' && PERSONAL_FIELDS.some(field => field.key === key && field.automatic)) return
      if (section === 'fields' && key === 'spouse_age') {
        this.$store.commit('set_personal_sheet_spouse', value)
        return
      }
      this.$store.commit('set_personal_sheet_field', { section, key, value: value == null ? '' : String(value) }) },
    valid(value, signed = false) {
      return value === '' || value == null || parseSheetAmount(value, signed) !== null || (signed ? '9桁以内の整数を入力してください' : '0以上・9桁以内の整数を入力してください')
    },
    yen(value) { return value === null ? '—' : value.toLocaleString('ja-JP') + '円' }
  }
}
</script>

<style scoped>
.personal-sheet-panel ::v-deep .error--text { color: #b71c1c !important; caret-color: #b71c1c !important; }
.personal-sheet-tab { position: fixed; right: 0; top: 45%; z-index: 6; writing-mode: vertical-rl; height: auto !important; min-width: 40px !important; padding: 16px 10px !important; border-radius: 8px 0 0 8px; }
.sheet-calculation-note { color: #b71c1c; background: #fff3e0; border-left: 4px solid #b71c1c; padding: 12px; margin: 0 0 16px; font-size: 14px; line-height: 1.6; font-weight: 700; overflow-wrap: anywhere; }
.sheet-table-wrap { overflow-x: auto; }
.sheet-table { width: 100%; min-width: 620px; border-collapse: collapse; color: #263238; }
.sheet-table th, .sheet-table td { padding: 10px 8px; border: 1px solid #cfd8dc; }
.sheet-table thead th { background: #eceff1; }
.sheet-table tbody th { width: 28%; text-align: left; font-weight: 500; }
.sheet-table td { text-align: right; white-space: nowrap; }
.sheet-table .sheet-input-cell { width: 180px; padding-bottom: 0; }
.sheet-rate { display: block; font-size: 12px; color: #546e7a; }
.sheet-totals { background: #f5f7f8; flex-shrink: 0; color: #263238; }
@media (max-width: 599px) {
  .sheet-table { min-width: 0; }
  .sheet-table thead { display: none; }
  .sheet-table tbody { display: block; }
  .sheet-table tr { display: grid; grid-template-columns: 1fr 1fr; margin-bottom: 16px; border: 1px solid #cfd8dc; border-radius: 4px; }
  .sheet-table th, .sheet-table td { border: 0; }
  .sheet-table tbody th { grid-column: 1 / -1; width: auto; order: 1; background: #eceff1; }
  .sheet-lower { order: 2; }
  .sheet-upper { order: 3; }
  .sheet-lower::before { content: '下限'; float: left; }
  .sheet-upper::before { content: '上限'; float: left; }
  .sheet-table .sheet-input-cell { grid-column: 1 / -1; width: auto; order: 4; }
}
</style>

<style>
@media (min-width: 960px) {
  .personal-sheet-dialog { margin: 0 0 0 auto !important; height: 100%; max-height: 100% !important; border-radius: 0 !important; }
  .personal-sheet-dialog .personal-sheet-panel { height: 100%; border-radius: 0; }
}
</style>
